import { useState, useEffect, useRef, useCallback } from 'react'
import { useDualWallet } from './DualWalletContext'
import {
  findV2PoolsRest, quoteV2Deposit, openV2Position, refreshLpPositions,
  getV2Balances, rawToUiString, v2DepositBounds, checkV2Signature,
  claimV2Fees, removeV2Position, SOL_RESERVE,
  findLpPools, claimDlmmFees, closeDlmmPositions,
} from '../utils/meteoraLp'
import DlmmDepositForm from './DlmmDepositForm'

// ─── MeteoraLpPanel: open a DAMM v2 LP position from a holdings row ───
//
// Self-contained on purpose. WalletHoldings.jsx is 2800 lines; adding a
// pool picker, a debounced quote and a transaction flow inline would bury
// all three. This owns its own state and is mounted per-row only while
// expanded, so a collapsed row costs nothing.
//
// NOTHING here loads at import time. The SDK sits behind dynamic import()
// inside meteoraLp's action layer, so the chart path never pulls it — the
// panel only triggers that load when a pool is actually selected.
//
// TWO FACTS THE UI HAS TO RESPECT, both learned the expensive way:
//
// 1. DISCOVERY IS REST, NOT SDK. The SDK's fetchPoolStatesByTokenMint ranks
//    by PoolState.liquidity — a Q64 number whose scale depends on the pair's
//    price, so it is not comparable between pools. On MET it ranked a
//    micro-priced pair above MET-USDC at $2.1M TVL, and returned 364 rows.
//    REST gives USD TVL and a sane top-N.
//
// 2. DEPOSITS ARE TWO-SIDED. createPositionAndAddLiquidity takes BOTH
//    maxAmountTokenA and maxAmountTokenB, so opening a position needs both
//    tokens in the wallet. Buying a coin in Cheddar gets you one side only.
//    The panel states the pair requirement up front rather than letting the
//    user discover it when the transaction fails — and checks both balances
//    (plus SOL for rent) before asking for a signature.
//
// Also exported: MeteoraV2Actions — Claim / Remove on an existing DAMM v2
// position row, each behind an inline confirm step.

const fmtTvl = (n) => {
  if (!Number.isFinite(n) || n <= 0) return '—'
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`
  return `$${n.toFixed(0)}`
}

// Never exponent notation — "max 1.2e-5" read as garbage and, worse,
// String(1.2e-5) fed back into the input couldn't be parsed by toBase.
const fmtAmt = (n) => {
  if (n == null || !Number.isFinite(n)) return '—'
  if (n === 0) return '0'
  if (n < 0.0001) return n.toLocaleString('en-US', { maximumSignificantDigits: 3, useGrouping: false })
  if (n < 1) return n.toFixed(6).replace(/0+$/, '')
  if (n < 1000) return n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

const STATUS_LABEL = {
  quoting: 'Quoting…',
  checking: 'Checking balances…',
  building: 'Building transaction…',
  simulating: 'Simulating…',
  signing: 'Waiting for signature…',
  sending: 'Sending…',
  confirming: 'Confirming…',
  done: 'Confirmed',
}

const QUOTE_MAX_AGE_MS = 30_000
const EXPIRED_MSG = 'Not landed before its blockhash expired — it can no longer land. Safe to retry.'

const Solscan = ({ sig }) => (
  <a href={`https://solscan.io/tx/${sig}`} target="_blank" rel="noreferrer">{sig.slice(0, 8)}…</a>
)

const errText = (e) => {
  const m = String(e?.message || e)
  return /reject|cancel|denied|declined/i.test(m) ? 'Cancelled in wallet — nothing was sent.' : m
}

/** Why this deposit can't go through with the wallet as it stands, or null.
 *  Compares against the quote's bounds — the most the transaction can pull. */
function fundsProblem(q, bal, symbol, pairSymbol) {
  if (!q || !bal) return null
  const b = v2DepositBounds(q)
  const tokenDec = q.isTokenA ? q.infoA.decimals : q.infoB.decimals
  const pairDec = q.isTokenA ? q.infoB.decimals : q.infoA.decimals
  const t = bal[q.isTokenA ? q.infoA.mint : q.infoB.mint]
  if (t && b.tokenRaw > t.raw) {
    return `Not enough ${symbol}: need ${fmtAmt(Number(rawToUiString(b.tokenRaw, tokenDec)))}, wallet has ${fmtAmt(Number(rawToUiString(t.raw, tokenDec)))}.`
  }
  const p = bal[q.pairMint]
  if (p && b.pairRaw > p.raw) {
    return q.pairIsSol
      ? `Not enough SOL: need ${fmtAmt(Number(rawToUiString(b.pairRaw, 9)))} SOL + ~${SOL_RESERVE} SOL for rent/fees, wallet has ${fmtAmt(Number(rawToUiString(bal.lamports, 9)))} SOL.`
      : `Not enough ${pairSymbol}: need ${fmtAmt(Number(rawToUiString(b.pairRaw, pairDec)))}, wallet has ${fmtAmt(Number(rawToUiString(p.raw, pairDec)))}.`
  }
  if (bal.lamports < BigInt(Math.round(SOL_RESERVE * 1e9))) {
    return `Keep ~${SOL_RESERVE} SOL for rent and fees (rent comes back when the position is closed) — wallet has ${fmtAmt(Number(rawToUiString(bal.lamports, 9)))} SOL.`
  }
  return null
}

export default function MeteoraLpPanel({ mint, symbol, uiBalance, refreshKey = 0, onClose, onOpened }) {
  const { publicKey, signTransaction, isReady } = useDualWallet()

  const [pools, setPools] = useState(null)     // null = loading, [] = none
  const [poolsErr, setPoolsErr] = useState(null)
  const [poolsTick, setPoolsTick] = useState(0)
  const [poolsNote, setPoolsNote] = useState(null)  // one source failed, other ok
  const [selected, setSelected] = useState(null)
  const [amount, setAmount] = useState('')
  const [quote, setQuote] = useState(null)
  const [quoteErr, setQuoteErr] = useState(null)
  const [quoting, setQuoting] = useState(false)
  const [balances, setBalances] = useState(null)   // last SUCCESSFUL read only
  const [balTick, setBalTick] = useState(0)
  const [balLoading, setBalLoading] = useState(false)
  const [balErr, setBalErr] = useState(null)
  const [status, setStatus] = useState(null)
  const [sentSig, setSentSig] = useState(null)
  const [pending, setPending] = useState(null)  // { signature } while unresolved
  const [result, setResult] = useState(null)
  const [notice, setNotice] = useState(null)
  const [error, setError] = useState(null)

  const aliveRef = useRef(true)
  const inFlightRef = useRef(false)
  useEffect(() => () => { aliveRef.current = false }, [])

  // ─── Pool discovery (REST, TVL-ranked) ───
  useEffect(() => {
    let alive = true
    setPools(null); setPoolsErr(null); setPoolsNote(null)
    // DAMM v2 + DLMM merged (findLpPools), TVL-ranked, $0 pools flagged.
    findLpPools(mint)
      .then(({ rows, partialErr }) => {
        if (!alive) return
        setPools(rows)
        setPoolsNote(partialErr)
        if (rows.length) setSelected(rows[0])
      })
      .catch(e => { if (alive) { setPools([]); setPoolsErr(String(e.message || e)) } })
    return () => { alive = false }
  }, [mint, poolsTick])

  // ─── Real wallet balances (token + pair + SOL) ───
  // Max uses the ON-CHAIN token balance, not the row's display number (the
  // current-token row shows the trade record's amount, which can differ).
  //
  // Re-read when: the panel opens (mount), the wallet address appears or
  // changes (after a reload it resolves async — local unlock / Privy boot),
  // the pool's pair changes, holdings refresh (refreshKey), the tab comes
  // back, or the user retries. An RPC error is NOT a zero: the last good
  // read is kept, the error is shown, and it retries (1.5s, 4s, 10s).
  // getV2Balances reads by MINT (getParsedTokenAccountsByOwner {mint}), which
  // returns every account for that mint under either token program.
  const owner = publicKey ? publicKey.toString() : null
  const pairMint = selected?.pairMint || null
  const balKey = `${owner}|${mint}`
  const balKeyRef = useRef(balKey)
  if (balKeyRef.current !== balKey) {
    // Different wallet or token: the old numbers are someone else's.
    balKeyRef.current = balKey
    if (balances) setBalances(null)
  }
  useEffect(() => {
    if (!owner) return
    let alive = true
    let timer = 0
    let tries = 0
    const read = () => {
      setBalLoading(true)
      getV2Balances(owner, [mint, pairMint].filter(Boolean))
        .then(b => { if (alive) { setBalances(b); setBalErr(null); setBalLoading(false) } })
        .catch(e => {
          if (!alive) return
          setBalErr(String(e?.message || e))
          setBalLoading(false)
          if (tries < 3) timer = setTimeout(read, [1500, 4000, 10000][tries++])
        })
    }
    read()
    return () => { alive = false; clearTimeout(timer) }
  }, [owner, mint, pairMint, balTick, refreshKey])
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') setBalTick(t => t + 1) }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])

  // ─── Debounced quote ───
  // The quote is a chain read (fetchPoolState + two mint accounts), so it
  // fires on a pause in typing, never per keystroke. `quoting` is reset on
  // EVERY run, so clearing the box can't leave "Quoting…" stuck on screen.
  useEffect(() => {
    setQuote(null); setQuoteErr(null); setQuoting(false); setNotice(null)
    const amt = parseFloat(amount)
    if (!selected || selected.type === 'dlmm' || !Number.isFinite(amt) || amt <= 0) return
    let alive = true
    setQuoting(true)
    const t = setTimeout(() => {
      quoteV2Deposit({ pool: selected.pool, mint, uiAmount: amount })
        .then(q => { if (alive) { setQuote(q); setQuoting(false) } })
        .catch(e => { if (alive) { setQuoteErr(String(e.message || e)); setQuoting(false) } })
    }, 450)
    return () => { alive = false; clearTimeout(t) }
  }, [selected, amount, mint])

  const tokenBal = balances?.[mint]
  // Until the first successful on-chain read there is no max and no "over
  // balance" — the label says "checking" instead of a 0. The row's number is
  // used only when the read has FAILED (labelled by the error note).
  const balPending = !tokenBal && !balErr
  const maxStr = tokenBal
    ? rawToUiString(tokenBal.raw, tokenBal.decimals || 0)
    : (balErr && uiBalance != null && Number.isFinite(uiBalance) && uiBalance > 0
        ? uiBalance.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 9 })
        : null)
  const maxNum = maxStr != null ? Number(maxStr) : null
  const pairSymbol = selected?.pairSymbol || 'pair token'

  const overBalance = Number.isFinite(parseFloat(amount)) && maxNum != null
    && parseFloat(amount) > maxNum
  const fundsErr = overBalance ? null : fundsProblem(quote, balances, symbol, pairSymbol)

  const succeed = useCallback((r) => {
    setResult(r)
    setPending(null)
    setAmount('')            // button returns to "Add liquidity" for the next one
    setBalTick(t => t + 1)
    if (owner) {
      try { refreshLpPositions(owner) } catch {}
      setTimeout(() => { try { refreshLpPositions(owner) } catch {} }, 6000)
    }
    onOpened?.(r)
  }, [owner, onOpened])

  const handleOpen = useCallback(async () => {
    if (!selected || !quote || !isReady || !owner || inFlightRef.current) return
    inFlightRef.current = true
    setError(null); setResult(null); setNotice(null); setSentSig(null)
    const onStatus = (s, extra) => {
      if (!aliveRef.current) return
      setStatus(s)
      if (extra?.signature) setSentSig(extra.signature)
    }
    try {
      let q = quote
      // A stale quote is refreshed VISIBLY: if the pair side moved, stop and
      // show the new number instead of signing something the user didn't see.
      if (Date.now() - (q.ts || 0) > QUOTE_MAX_AGE_MS) {
        setStatus('quoting')
        const fresh = await quoteV2Deposit({ pool: selected.pool, mint, uiAmount: amount })
        if (!aliveRef.current) return
        const drift = Math.abs(fresh.pairUiAmount - q.pairUiAmount) / (q.pairUiAmount || 1)
        setQuote(fresh)
        if (drift > 0.005) {
          setNotice(`Quote refreshed: now ${fmtAmt(fresh.pairUiAmount)} ${pairSymbol} (was ${fmtAmt(q.pairUiAmount)}). Review and press again.`)
          setStatus(null)
          return
        }
        q = fresh
      }
      // Fresh balances right before signing — a clear message beats a
      // failed simulation.
      setStatus('checking')
      const bal = await getV2Balances(owner, [mint, q.pairMint])
      if (!aliveRef.current) return
      setBalances(bal)
      const why = fundsProblem(q, bal, symbol, pairSymbol)
      if (why) { setError(why); setStatus(null); return }

      const r = await openV2Position({ quote: q, owner, signTransaction, onStatus })
      if (!aliveRef.current) return
      setStatus(null)
      if (r.state === 'confirmed') succeed(r)
      else if (r.state === 'pending') setPending(r)
      else setError(EXPIRED_MSG)
    } catch (e) {
      if (aliveRef.current) {
        setError(errText(e))
        if (e?.signature) setSentSig(e.signature)
        setStatus(null)
      }
    } finally {
      inFlightRef.current = false
    }
  }, [selected, quote, isReady, owner, mint, amount, symbol, pairSymbol, signTransaction, succeed])

  const recheck = useCallback(async () => {
    if (!pending) return
    setNotice(null)
    try {
      const s = await checkV2Signature(pending.signature)
      if (!aliveRef.current) return
      if (s === 'confirmed') succeed(pending)
      else if (s === 'failed') { setSentSig(pending.signature); setPending(null); setError('Transaction failed on-chain — nothing was deposited.') }
      else setNotice('Still not confirmed. Check Solscan before trying again.')
    } catch (e) {
      if (aliveRef.current) setNotice(`Status check failed: ${String(e.message || e)}`)
    }
  }, [pending, succeed])

  const busy = !!status && status !== 'done'
  const bounds = quote ? v2DepositBounds(quote) : null

  return (
    <div className="wallet-lp-open">
      <div className="wallet-lp-open-head">
        <span className="wallet-lp-tag">+ LP</span>
        <span className="wallet-lp-open-title">{symbol || mint.slice(0, 4)} · Meteora LP</span>
        <button className="wallet-lp-open-x" onClick={onClose} disabled={busy}>×</button>
      </div>

      {/* ── Pool picker ── */}
      {pools === null && <div className="wallet-lp-note">Finding pools…</div>}
      {pools?.length === 0 && (
        <div className={`wallet-lp-note${poolsErr ? ' err' : ''}`}>
          {poolsErr || 'No Meteora pool (DLMM or DAMM v2) exists for this token.'}
          {poolsErr && (
            <button className="wallet-lp-refresh" onClick={() => setPoolsTick(t => t + 1)}>retry ↻</button>
          )}
        </div>
      )}

      {!!pools?.length && (
        <>
          <label className="wallet-lp-label">Pool</label>
          {/* Wrapped so the chevron can be ours: a native select arrow can't
              be styled, which is where the grey hover box came from. */}
          <div className="wallet-lp-select-wrap">
            <select
              className="wallet-lp-select"
              value={selected?.pool || ''}
              disabled={busy || !!pending}
              onChange={(e) => { setAmount(''); setSelected(pools.find(p => p.pool === e.target.value) || null) }}
            >
              {pools.map(p => (
                <option key={p.pool} value={p.pool}>
                  {p.type === 'dlmm' ? 'DLMM' : 'DAMM v2'} · {p.name || `${symbol}-${p.pairSymbol}`} · {p.unindexed ? 'not indexed yet' : `${fmtTvl(p.tvl)} TVL`}
                  {p.feePct != null ? ` · ${p.feePct}%` : ''}{p.binStep != null ? ` · bin ${p.binStep}` : ''}
                </option>
              ))}
            </select>
          </div>
          {poolsNote && <div className="wallet-lp-note err">{poolsNote} — showing the rest.</div>}
          {selected?.unindexed && (
            <div className="wallet-lp-note err">This pool shows $0 TVL — likely brand new / not indexed yet, or empty. Check it on Meteora before depositing.</div>
          )}

          {selected?.type === 'dlmm' ? (
            <DlmmDepositForm key={selected.pool} pool={selected} mint={mint} symbol={symbol}
              balances={balances} onBalancesStale={() => setBalTick(t => t + 1)} onOpened={succeed} />
          ) : (<>
          {/* ── Amount ── */}
          <label className="wallet-lp-label">
            Deposit {symbol}
            {maxStr != null && (
              <button
                className="wallet-lp-max"
                disabled={busy || !!pending}
                onClick={() => setAmount(maxStr)}
              >max {fmtAmt(maxNum)}</button>
            )}
            {maxStr == null && balPending && owner && (
              <span className="wallet-lp-max" style={{ opacity: 0.6, cursor: 'default' }}>checking balance…</span>
            )}
          </label>
          <input
            className="wallet-lp-input"
            inputMode="decimal"
            placeholder="0.0"
            value={amount}
            disabled={busy || !!pending}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
          />

          {/* ── Pair requirement: the thing that surprises people ── */}
          {quoting && <div className="wallet-lp-note">Quoting…</div>}
          {quoteErr && <div className="wallet-lp-note err">{quoteErr}</div>}
          {quote && bounds && (
            <div className="wallet-lp-quote">
              <div className="wallet-lp-quote-row">
                <span>Also required</span>
                <strong>{fmtAmt(quote.pairUiAmount)} {pairSymbol}</strong>
              </div>
              <div className="wallet-lp-quote-note">
                Both sides are deposited. You need {pairSymbol} in
                this wallet as well{selected?.concentrated ? ' — ratio is set by the pool’s range, not spot price.' : '.'}
                {' '}~1% slippage buffer: neither side can take more than shown.
              </div>
            </div>
          )}

          {balErr && !tokenBal && (
            <div className="wallet-lp-note err">
              Couldn’t read wallet balances ({balErr}){balLoading ? ' — retrying…' : ''}{' '}
              <button className="wallet-lp-refresh" onClick={() => setBalTick(t => t + 1)}>retry ↻</button>
            </div>
          )}
          {overBalance && (
            <div className="wallet-lp-note err">Amount exceeds your {symbol} balance.</div>
          )}
          {fundsErr && !error && <div className="wallet-lp-note err">{fundsErr}</div>}
          {notice && <div className="wallet-lp-note">{notice}</div>}

          {/* ── Action ── */}
          {!isReady && <div className="wallet-lp-note">Wallet locked — unlock or connect to continue.</div>}

          <button
            className="wallet-lp-go"
            disabled={!quote || !isReady || busy || !!pending || overBalance || !!fundsErr}
            onClick={handleOpen}
          >
            {busy ? (STATUS_LABEL[status] || 'Working…') : pending ? 'Pending — check Solscan' : 'Add liquidity'}
          </button>

          {busy && sentSig && <div className="wallet-lp-note">Sent · <Solscan sig={sentSig} /></div>}
          {pending && (
            <div className="wallet-lp-note">
              Pending — not confirmed yet. Check <Solscan sig={pending.signature} /> before retrying.{' '}
              <button className="wallet-lp-refresh" onClick={recheck}>re-check ↻</button>
            </div>
          )}
          {error && (
            <div className="wallet-lp-note err">
              {error}{!busy && sentSig && <> · <Solscan sig={sentSig} /></>}
            </div>
          )}
          {result && (
            <div className="wallet-lp-note ok">
              Position opened · <Solscan sig={result.signature} />
            </div>
          )}
          </>)}
        </>
      )}
    </div>
  )
}

// ─── Claim / Remove on an existing DAMM v2 position ───
// Rendered under each v2 position line. Every action is two clicks: the
// first shows what will happen, the second signs. Same send path as open
// (simulate before signing, confirm with an err check).
export function MeteoraV2Actions({ lp }) {
  const { publicKey, signTransaction, isReady } = useDualWallet()
  const [confirm, setConfirm] = useState(null)   // 'claim' | 'remove' | null
  const [action, setAction] = useState(null)
  const [status, setStatus] = useState(null)
  const [sig, setSig] = useState(null)
  const [done, setDone] = useState(null)         // { kind, signature, state }
  const [error, setError] = useState(null)
  const aliveRef = useRef(true)
  useEffect(() => () => { aliveRef.current = false }, [])

  const owner = publicKey ? publicKey.toString() : null
  const busy = !!status
  const blocked = busy || done?.state === 'pending'
  const hasFees = (lp.feeX || 0) > 0 || (lp.feeY || 0) > 0
  const fees = `${fmtAmt(lp.feeX)} ${lp.symX} + ${fmtAmt(lp.feeY)} ${lp.symY}`
  const amounts = lp.amountX != null ? `~${fmtAmt(lp.amountX)} ${lp.symX} + ${fmtAmt(lp.amountY)} ${lp.symY}` : 'all liquidity'

  const run = async (kind) => {
    if (!owner || busy) return
    setConfirm(null); setError(null); setDone(null); setSig(null); setAction(kind)
    const onStatus = (s, extra) => {
      if (!aliveRef.current) return
      if (s !== 'done' && s !== 'pending' && s !== 'expired') setStatus(s)
      if (extra?.signature) setSig(extra.signature)
    }
    try {
      const fn = kind === 'claim' ? claimV2Fees : removeV2Position
      const r = await fn({ lp, owner, signTransaction, onStatus })
      if (!aliveRef.current) return
      setStatus(null)
      if (r.state === 'expired') setError(EXPIRED_MSG)
      else setDone({ kind, ...r })
      if (r.state === 'confirmed') {
        // Short delay so the confirmation is readable before a removed
        // position's row disappears on refresh.
        setTimeout(() => { try { refreshLpPositions(owner) } catch {} }, 2500)
      }
    } catch (e) {
      if (!aliveRef.current) return
      setStatus(null)
      setError(errText(e))
      if (e?.signature) setSig(e.signature)
    }
  }

  const recheck = async () => {
    if (!done?.signature) return
    const s = await checkV2Signature(done.signature).catch(() => 'unknown')
    if (!aliveRef.current) return
    if (s === 'confirmed') { setDone({ ...done, state: 'confirmed' }); try { refreshLpPositions(owner) } catch {} }
    else if (s === 'failed') { setDone(null); setSig(done.signature); setError('Transaction failed on-chain.') }
  }

  const label = (kind, idle) => (busy && action === kind ? (STATUS_LABEL[status] || 'Working…') : idle)
  const btnStyle = (disabled) => (disabled ? { opacity: 0.4, cursor: 'default' } : undefined)
  const claimOff = blocked || !isReady || !hasFees
  const removeOff = blocked || !isReady || lp.permanentLocked

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {confirm && (
        <div className="wallet-lp-note">
          {confirm === 'claim'
            ? `Claim ${fees} to this wallet?`
            : `Withdraw ${amounts} plus unclaimed fees and close the position?`}
        </div>
      )}
      <div className="wallet-lp-actions" style={{ gap: 4 }}>
        {confirm ? (
          <>
            <button className="wallet-lp-add" onClick={() => run(confirm)}>Confirm</button>
            <button className="wallet-lp-add" onClick={() => setConfirm(null)}>Cancel</button>
          </>
        ) : (
          <>
            <button className="wallet-lp-add" disabled={claimOff} style={btnStyle(claimOff)}
              title={hasFees ? `Claim ${fees}` : 'No unclaimed fees'}
              onClick={() => setConfirm('claim')}>{label('claim', 'Claim fees')}</button>
            <button className="wallet-lp-add" disabled={removeOff} style={btnStyle(removeOff)}
              title={lp.permanentLocked ? 'Permanently locked — cannot be withdrawn' : 'Remove all liquidity and close'}
              onClick={() => setConfirm('remove')}>{label('remove', 'Remove')}</button>
          </>
        )}
      </div>
      {busy && sig && <div className="wallet-lp-note">Sent · <Solscan sig={sig} /></div>}
      {error && <div className="wallet-lp-note err">{error}{sig && <> · <Solscan sig={sig} /></>}</div>}
      {done?.state === 'confirmed' && (
        <div className="wallet-lp-note ok">
          {done.kind === 'claim' ? 'Fees claimed' : 'Position closed'} · <Solscan sig={done.signature} />
        </div>
      )}
      {done?.state === 'pending' && (
        <div className="wallet-lp-note">
          Pending — check <Solscan sig={done.signature} /> before retrying.{' '}
          <button className="wallet-lp-refresh" onClick={recheck}>re-check ↻</button>
        </div>
      )}
    </div>
  )
}

// ─── Claim / Close on an existing DLMM pool row (lp-dlmm-close-20261015) ───
// Acts on every position this wallet holds in lp.pool (read fresh from chain).
// Close = withdraw 100% + claim + close, possibly several transactions, sent
// in order; each is simulated, signed and confirmed like the deposit.
export function MeteoraDlmmActions({ lp }) {
  const { publicKey, signTransaction, isReady } = useDualWallet()
  const [confirm, setConfirm] = useState(null)   // 'claim' | 'close' | null
  const [action, setAction] = useState(null)
  const [status, setStatus] = useState(null)
  const [step, setStep] = useState('')
  const [sig, setSig] = useState(null)
  const [done, setDone] = useState(null)
  const [error, setError] = useState(null)
  const aliveRef = useRef(true)
  useEffect(() => () => { aliveRef.current = false }, [])
  const owner = publicKey ? publicKey.toString() : null
  const busy = !!status
  const blocked = busy || done?.state === 'pending'
  const n = lp.positionCount || 1

  const run = async (kind) => {
    if (!owner || busy || !lp.pool) return
    setConfirm(null); setError(null); setDone(null); setSig(null); setStep(''); setAction(kind)
    const onStatus = (s, extra) => {
      if (!aliveRef.current) return
      if (s !== 'done' && s !== 'pending' && s !== 'expired') setStatus(s)
      if (extra?.signature) setSig(extra.signature)
      if (extra?.step != null) setStep(extra.step)
    }
    try {
      const fn = kind === 'claim' ? claimDlmmFees : closeDlmmPositions
      const r = await fn({ pool: lp.pool, owner, signTransaction, onStatus })
      if (!aliveRef.current) return
      setStatus(null)
      if (r.state === 'expired') setError(`${r.done ? `${r.done}/${r.total} sent OK; ` : ''}${EXPIRED_MSG}`)
      else setDone({ kind, ...r })
      if (r.done > 0 || r.state === 'confirmed') {
        try { refreshLpPositions(owner) } catch {}
        setTimeout(() => { try { refreshLpPositions(owner) } catch {} }, 4000)
      }
    } catch (e) {
      if (!aliveRef.current) return
      setStatus(null)
      setError(errText(e))
      if (e?.signature) setSig(e.signature)
      try { refreshLpPositions(owner) } catch {}
    }
  }

  const recheck = async () => {
    if (!done?.signature) return
    const s = await checkV2Signature(done.signature).catch(() => 'unknown')
    if (!aliveRef.current) return
    if (s === 'confirmed') {
      setDone({ ...done, state: 'confirmed' })
      try { refreshLpPositions(owner) } catch {}
      if (done.done + 1 < done.total) setError('That step landed, but later steps were not sent. Press the button again to finish.')
    } else if (s === 'failed') { setDone(null); setSig(done.signature); setError('Transaction failed on-chain.') }
  }

  const label = (kind, idle) => (busy && action === kind ? `${STATUS_LABEL[status] || 'Working…'}${step}` : idle)
  const off = blocked || !isReady || !lp.pool
  const st = off ? { opacity: 0.4, cursor: 'default' } : undefined

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {confirm && (
        <div className="wallet-lp-note">
          {confirm === 'claim'
            ? `Claim swap fees + rewards from ${n > 1 ? `${n} positions` : 'this position'}?`
            : `Withdraw 100% of liquidity, claim fees and close ${n > 1 ? `all ${n} positions` : 'the position'} (rent refunded)? May need several signatures.`}
        </div>
      )}
      <div className="wallet-lp-actions" style={{ gap: 4 }}>
        {confirm ? (
          <>
            <button className="wallet-lp-add" onClick={() => run(confirm)}>Confirm</button>
            <button className="wallet-lp-add" onClick={() => setConfirm(null)}>Cancel</button>
          </>
        ) : (
          <>
            <button className="wallet-lp-add" disabled={off} style={st} onClick={() => setConfirm('claim')}>{label('claim', 'Claim fees')}</button>
            <button className="wallet-lp-add" disabled={off} style={st} onClick={() => setConfirm('close')}>{label('close', 'Close')}</button>
          </>
        )}
        {lp.pool && (
          <a className="wallet-lp-fees" style={{ fontSize: 8, alignSelf: 'center' }} href={`https://app.meteora.ag/dlmm/${lp.pool}`}
            target="_blank" rel="noreferrer">Meteora ↗</a>
        )}
      </div>
      {busy && sig && <div className="wallet-lp-note">Sent{step} · <Solscan sig={sig} /></div>}
      {error && <div className="wallet-lp-note err">{error}{sig && <> · <Solscan sig={sig} /></>}</div>}
      {done?.state === 'confirmed' && (
        <div className="wallet-lp-note ok">
          {done.kind === 'claim' ? 'Fees claimed' : 'Position closed'}{done.total > 1 ? ` (${done.total} tx)` : ''} · <Solscan sig={done.signature} />
        </div>
      )}
      {done?.state === 'pending' && (
        <div className="wallet-lp-note">
          Pending{done.total > 1 ? ` (step ${done.done + 1}/${done.total})` : ''} — check <Solscan sig={done.signature} /> before retrying.{' '}
          <button className="wallet-lp-refresh" onClick={recheck}>re-check ↻</button>
        </div>
      )}
    </div>
  )
}
